import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Envelope, ExternalLink, LayersFront, WhatsappChat } from '@factorialco/f0-react/icons/app'
import copy from 'copy-to-clipboard'
import {
  AtsJobApplicationReviewerHeaderFeaturesFragment,
  AtsJobApplicationReviewerHeaderFragment,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'
import app from 'modules/core/store/models/app'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { timeAgoWhenRecent } from 'modules/ats/lib/dates'
import { ellipsisCandidateData } from 'modules/ats/lib/utils/candidate'

interface Props {
  applicationData: AtsJobApplicationReviewerHeaderFragment
  features: AtsJobApplicationReviewerHeaderFeaturesFragment
}

export const JobApplicationReviewHeader: React.FC<Props> = ({ applicationData, features }) => {
  const isWhatsappFeatureEnabled =
    features.enabledFeatures.nodes.find((n) => n.name === 'ats_whatsapp')?.enabled ?? false

  const {
    candidate,
    createdAt,
    jobPosting,
    personalUrl,
    photo,
    qualified,
    rejectionReason,
    applicationPhase,
  } = applicationData
  const { email, phoneNumber, firstName, lastName, fullName } = candidate

  let personalUrlLink = null
  if (personalUrl) {
    personalUrlLink = isAbsoluteURL(personalUrl) ? personalUrl : `https://${personalUrl}`
  }

  const copyUrl = async (url: string) => {
    if (copy(url)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.copied'),
      })
    }
  }

  const onClickOpenWhatsappChat = () => {
    track(trackingEvents.ats.openWhatsappChatWithCandidate)
    window.open('https://wa.me/' + phoneNumber, '_blank', 'noopener,noreferrer')
  }

  return (
    <ResourceHeader
      title={fullName}
      description={jobPosting.title}
      avatar={{
        type: 'person',
        firstName: firstName,
        lastName: lastName,
        src: photo ?? undefined,
      }}
      status={
        qualified
          ? undefined
          : {
              label: i18n.t('ats.application_details.header.status_label'),
              text: rejectionReason?.reason
                ? rejectionReason.reason
                : i18n.t('ats.application_details.rejected_status'),
              variant: 'critical',
            }
      }
      metadata={[
        !!qualified && {
          label: i18n.t('ats.application_details.header.phase_label'),
          value: {
            type: 'dot-tag',
            label: applicationPhase.name,
            color: phaseColor(applicationPhase.hiringStage?.name || 'default'),
          },
        },
        !!email && {
          label: i18n.t('ats.application_details.header.email_label'),
          value: {
            type: 'text',
            content: ellipsisCandidateData(email) || '',
          },
          actions: [
            {
              label: i18n.t('ats.application_details.header.send_email'),
              icon: Envelope,
              onClick: () => window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer'),
            },
            {
              label: i18n.t('common.copy'),
              icon: LayersFront,
              onClick: () => copyUrl(email),
            },
          ],
        },
        !!phoneNumber && {
          label: i18n.t('ats.application_details.header.phone_label'),
          value: { type: 'text', content: phoneNumber },
          actions: [
            !!isWhatsappFeatureEnabled && {
              label: i18n.t('ats.application_details.header.chat_on_whatsapp'),
              icon: WhatsappChat,
              onClick: () => onClickOpenWhatsappChat(),
            },
          ].filter(Boolean),
        },
        createdAt && {
          label: i18n.t('ats.application_details.header.added_at_label'),
          value: { type: 'text', content: timeAgoWhenRecent(createdAt) },
        },
        !!personalUrlLink && {
          label: i18n.t('ats.application_details.header.link_label'),
          value: {
            type: 'text',
            content: ellipsisCandidateData(personalUrl) || '',
          },
          actions: [
            {
              label: i18n.t('ats.application_details.header.navigate_to_link'),
              icon: ExternalLink,
              onClick: () => window.open(personalUrlLink, '_blank', 'noopener,noreferrer'),
            },
            {
              label: i18n.t('common.copy'),
              icon: LayersFront,
              onClick: () => copyUrl(personalUrlLink),
            },
          ],
        },
      ]}
    />
  )
}
